import { Link, useParams } from 'react-router-dom';
import { getOrderById, getOrderFile } from '../../../api/ordersApi.js';
import { useRetryFetch } from '../hooks/useRetryFetch.js';
import StatusBadge from '../components/StatusBadge.jsx';
import Spinner from '../components/Spinner.jsx';
import WarningIcon from '../components/WarningIcon.jsx';
import Tooltip from '../components/Tooltip.jsx';
import { formatCurrency, formatDate } from '../utils/format.js';
import { classifyPortalError } from '../utils/apiError.js';
import { showErrorToast, showSuccessToast } from '../utils/toast.js';
import '../components/CommentModal.css';
import './OrderView.css';

function formatAddress(address) {
  if (!address) return null;
  return [address.line1, address.street, address.city, address.state, address.postalCode, address.country]
    .filter(Boolean)
    .join(', ');
}

function formatContact(order) {
  const contact = order.deliveryContact;
  const parts = [contact?.name, order.email ?? contact?.email, order.phoneNo ?? contact?.phoneNo].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

export default function OrderView() {
  const { orderId } = useParams();
  const { data: order, loading, error, errorMessage, isEntityNotFound, manualRetries, retry } = useRetryFetch(
    () => getOrderById(orderId),
    [orderId]
  );

  const handleFileDownload = async (fileName, isPOFile = false) => {
    if (!order?.id || !fileName) return;
    try {
      const res = await getOrderFile(order.id, fileName, isPOFile);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast(`Downloaded ${fileName}`);
    } catch (err) {
      showErrorToast(classifyPortalError(err).message || "Couldn't download the file. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="customer-portal-page-loader">
        <Spinner size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-portal-page-content">
        <div className="customer-portal-card customer-portal-card__error">
          <WarningIcon />
          <span>{errorMessage || "Couldn't load this order"}</span>
          {!isEntityNotFound && manualRetries > 0 && (
            <button
              onClick={retry}
              className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="customer-portal-page-content">
        <div className="customer-portal-orders-full-table__empty">Order {orderId} not found.</div>
      </div>
    );
  }

  const products = order.products || [];
  const productsTotal = products.reduce(
    (sum, p) => sum + Number(p.finalPrice ?? p.totalPrice ?? p.total ?? 0),
    0
  );

  return (
    <div className="customer-portal-page-content">
      <div className="customer-portal-order-view-header">
        <div>
          <div className="customer-portal-orders-header__breadcrumb">
            <Link to="/customer/orders">My Orders</Link>
            <Crumb />
            <span>Order Details</span>
          </div>
          <div className="customer-portal-orders-header__title">Order Details</div>
          <div className="customer-portal-order-view-header__subtitle">
            Order #{order.id} &nbsp;·&nbsp; {formatDate(order.createdAt)}
          </div>
        </div>
      </div>

      <div className="customer-portal-card customer-portal-order-view-summary">
        <div className="customer-portal-order-view-summary__title">Order Summary</div>
        <div className="customer-portal-order-view-summary__grid">
          <Field label="Customer Name" value={order.customer?.name} />
          <Field label="Delivery Address" value={formatAddress(order.deliveryAddress)} />
          <Field label="Purchase Order Date" value={formatDate(order.purchaseOrderDate)} />

          <Field label="Delivery Contact" value={formatContact(order)} />
          <Field label="Delivery Instruction" value={order.deliveryInstruction} />
          <Field label="Delivery Contact Email" value={order.email ?? order.deliveryContact?.email} />

          <Field label="Required Date" value={formatDate(order.requiredDate)} />
          <Field label="Phone No" value={order.phoneNo ?? order.deliveryContact?.phoneNo} />
          <Field label="Warehouse" value={order.warehouseCode} />

          <Field label="Purchase Order ID" value={order.purchaseOrderId} />
          <div className="customer-portal-order-view-field">
            <div className="customer-portal-order-view-field__label">Status</div>
            <StatusBadge status={order.status} />
          </div>
          <Field label="Order Date" value={formatDate(order.createdAt)} />

          <Field label="Total Amount" value={formatCurrency(order.total, order.currency)} strong />
          <Field label="Customer Entity" value={order.customer?.entity} />
          <Field label="Customer Code" value={order.customer?.customerCode} />
        </div>
      </div>

      {order.config && (
        <div className="customer-portal-card customer-portal-order-view-section">
          <div className="customer-portal-order-view-section__title">Comments</div>
          <div className="customer-portal-comment-content" dangerouslySetInnerHTML={{ __html: order.config }} />
        </div>
      )}

      <div className="customer-portal-card customer-portal-order-view-section">
        <div className="customer-portal-order-view-section__title">Purchase Order File</div>
        {order.purchaseOrderFileName ? (
          <button
            type="button"
            className="customer-portal-order-view-file-chip customer-portal-order-view-file-chip--downloadable"
            onClick={() => handleFileDownload(order.purchaseOrderFileName, true)}
            title="Download purchase order file"
          >
            <Tooltip content={order.purchaseOrderFileName} className="customer-portal-order-view-file-chip__name">{order.purchaseOrderFileName}</Tooltip>
            <DownloadIcon className="customer-portal-order-view-file-chip__icon" />
          </button>
        ) : (
          <div className="customer-portal-order-view-section__empty">No purchase order file uploaded</div>
        )}
      </div>

      <div className="customer-portal-card customer-portal-order-view-section">
        <div className="customer-portal-order-view-section__title">Additional Documents</div>
        {order.additionalFiles?.length ? (
          <ul className="customer-portal-order-view-doc-list">
            {order.additionalFiles.map((doc, idx) => {
              const fileName = typeof doc === 'string' ? doc : (doc.fileName ?? doc.name);
              return (
                <li key={idx}>
                  <button
                    type="button"
                    className="customer-portal-order-view-doc-card"
                    onClick={() => handleFileDownload(fileName, false)}
                    title={`Download ${fileName}`}
                  >
                    <Tooltip content={fileName} className="customer-portal-order-view-doc-list__name">{fileName}</Tooltip>
                    <DownloadIcon className="customer-portal-order-view-doc-card__icon" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="customer-portal-order-view-section__empty">No additional files uploaded</div>
        )}
      </div>

      <div className="customer-portal-card customer-portal-order-view-section">
        <div className="customer-portal-order-view-section__title">Products</div>
        <div className="customer-portal-order-view-section__subtitle">{products.length} items in this order</div>
        {products.length === 0 ? (
          <div className="customer-portal-order-view-section__empty">No products available</div>
        ) : (
          <>
            <div className="customer-portal-order-view-table-wrapper">
              <table className="customer-portal-order-view-table">
                <colgroup>
                  <col style={{ width: '42%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="customer-portal-orders-full-table__center">Product</th>
                    <th className="customer-portal-orders-full-table__center">Qty</th>
                    <th className="customer-portal-orders-full-table__right">Customer Price</th>
                    <th className="customer-portal-orders-full-table__right">Total</th>
                    <th className="customer-portal-orders-full-table__center">Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, idx) => {
                    const lineComment = p.comments || p.productComments;
                    const hasComment = Boolean(lineComment?.trim());
                    return (
                      <tr key={p.id ?? p.orderProductId ?? idx}>
                        <td className="customer-portal-orders-full-table__center">
                          <Tooltip as="div" className="customer-portal-order-view-table__product-code">{p.productCode}</Tooltip>
                          <Tooltip as="div" className="customer-portal-order-view-table__product-name">{p.description ?? p.productDescription}</Tooltip>
                        </td>
                        <Tooltip as="td" className="customer-portal-orders-full-table__center">{p.quantity}</Tooltip>
                        <Tooltip as="td" className="customer-portal-orders-full-table__right">{formatCurrency(p.customerPrice ?? p.unitPrice, order.currency)}</Tooltip>
                        <Tooltip as="td" className="customer-portal-orders-full-table__right">{formatCurrency(p.finalPrice ?? p.totalPrice ?? p.total, order.currency)}</Tooltip>
                        <td className="customer-portal-orders-full-table__center">
                          {hasComment ? (
                            <Tooltip
                              as="span"
                              className="customer-portal-order-detail__comment-icon customer-portal-order-detail__comment-icon--active"
                              content={lineComment}
                              forceOverflow
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </Tooltip>
                          ) : (
                            <span className="customer-portal-order-detail__comment-icon customer-portal-order-detail__comment-icon--inactive">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="customer-portal-order-view-table__total">
              Total: {formatCurrency(order.total ?? productsTotal, order.currency)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Crumb() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function DownloadIcon({ className }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function Field({ label, value, strong }) {
  return (
    <div className="customer-portal-order-view-field">
      <div className="customer-portal-order-view-field__label">{label}</div>
      <div className={`customer-portal-order-view-field__value${strong ? ' customer-portal-order-view-field__value--strong' : ''}`}>
        {value ?? '—'}
      </div>
    </div>
  );
}
